import { useEffect, useState } from "react";
import { Loader2, Radar, Satellite } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import LiveMap from "@/components/LiveMap";
import StopTimeline from "@/components/StopTimeline";
import { usePublicLive, PublicBusPopup, LiveBusCard, TrackCodeBox } from "@/components/PublicLive";
import { api } from "@/lib/api";

const ALL = "all";

function Filters({ stations, routes, station, route, setStation, setRoute }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Select value={station} onValueChange={setStation}>
        <SelectTrigger className="h-11 w-full rounded-xl bg-white sm:w-60" data-testid="live-station-filter"><SelectValue placeholder="All stations" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>All stations</SelectItem>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} · {s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={route} onValueChange={setRoute}>
        <SelectTrigger className="h-11 w-full rounded-xl bg-white sm:w-60" data-testid="live-route-filter"><SelectValue placeholder="All routes" /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>All routes</SelectItem>{routes.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function LivePage() {
  const [stations, setStations] = useState([]);
  const [station, setStation] = useState(ALL);
  const [route, setRoute] = useState(ALL);
  const [sel, setSel] = useState(null);
  const buses = usePublicLive(station === ALL ? null : station);
  useEffect(() => { api.get("/public/stations").then((r) => setStations(r.data)); }, []);
  const routes = [...new Set((buses || []).map((b) => b.route))].sort();
  const list = (buses || []).filter((b) => route === ALL || b.route === route);
  const cur = list.find((b) => b.trip_id === sel);
  return (
    <div className="mx-auto max-w-[1500px] space-y-5 px-4 py-6 sm:px-6" data-testid="live-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold text-[#182678]"><Radar className="h-7 w-7 text-[#0052FF]" />Live bus map</h1>
          <p className="text-sm text-slate-500">Every GPRTU bus on the road right now. Tap a bus for its next stop and ETA.</p>
        </div>
        <Filters stations={stations} routes={routes} station={station} route={route} setStation={(v) => { setStation(v); setSel(null); }} setRoute={(v) => { setRoute(v); setSel(null); }} />
      </div>
      {!buses ? <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : (
        <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
          <LiveMap buses={list} selected={sel} onSelect={(b) => setSel(b.trip_id)} renderPopup={(b) => <PublicBusPopup b={b} />} className="h-[420px] xl:h-[640px]" />
          <div className="space-y-3 xl:max-h-[640px] xl:overflow-y-auto">
            <TrackCodeBox />
            {cur && <div className="card-soft p-4" data-testid="live-bus-detail"><div className="mb-3 font-bold">{cur.bus_number} · {cur.route}</div><StopTimeline timeline={cur.timeline} /></div>}
            <div className="label-xs" data-testid="live-bus-count">{list.length} live bus{list.length === 1 ? "" : "es"}</div>
            {list.length ? <div className="space-y-2" data-testid="live-bus-list">{list.map((b) => <LiveBusCard key={b.trip_id} b={b} selected={sel === b.trip_id} onPick={(x) => setSel(x.trip_id)} />)}</div>
              : <div className="card-soft p-8 text-center text-sm text-slate-500" data-testid="live-empty">No buses match right now.</div>}
            <p className="flex items-center justify-center gap-1 text-center text-xs text-slate-400"><Satellite className="h-3.5 w-3.5" />Updates every 15 seconds</p>
          </div>
        </div>
      )}
    </div>
  );
}
